/**
 * finance-core — Tỷ số & điểm sức khoẻ tài chính (GĐ0B)
 *
 * MỘT bộ chấm điểm thay 4 bản độc lập của bản cũ. Mỗi thành phần có ngưỡng
 * theo chuẩn hoạch định tài chính cá nhân, và điểm tổng GIẢI THÍCH ĐƯỢC từng phần.
 * Thành phần thiếu dữ liệu (null) bị loại khỏi điểm và trọng số được chuẩn hoá lại —
 * không bao giờ "phạt" người dùng vì app chưa có dữ liệu.
 */

import { cardLinkedLoanBalance, cardOwnBalance } from './balance';

export interface HealthInput {
  avgMonthlyIncome: number;
  avgMonthlyExpense: number;
  /** Chi tiêu THIẾT YẾU trung bình tháng (theo nhãn danh mục). > 0 thì quỹ khẩn cấp tính trên số này; ngược lại dùng chi tiêu toàn bộ. */
  essentialMonthlyExpense?: number;
  /** Tiền quỹ khẩn cấp (xem emergencyFundBalance) */
  emergencyFund: number;
  /** Tổng khoản phải trả nợ mỗi tháng (thẻ tối thiểu + nợ vay) */
  monthlyDebtPayment: number;
  totalAssets: number;
  totalLiabilities: number;
}

export interface HealthRatios {
  /** % thu nhập còn lại; ÂM khi thâm hụt; null khi chưa có thu nhập */
  savingsRate: number | null;
  /** số tháng chi tiêu quỹ khẩn cấp trang trải được; null khi chưa có chi tiêu */
  emergencyMonths: number | null;
  /** Quỹ khẩn cấp đang chia cho chi thiết yếu hay chi toàn bộ (để ghi đúng nhãn) */
  emergencyBasis?: 'essential' | 'total' | null;
  /** % thu nhập dành trả nợ; null khi chưa có thu nhập */
  debtServiceRatio: number | null;
  /** nợ / tài sản (0–∞); null khi chưa có tài sản */
  debtToAssets: number | null;
}

export const healthRatios = (i: HealthInput): HealthRatios => {
  const essential = Number(i.essentialMonthlyExpense) || 0;
  const emergencyBasis = essential > 0 ? 'essential' : i.avgMonthlyExpense > 0 ? 'total' : null;
  const emergencyBase = essential > 0 ? essential : i.avgMonthlyExpense;
  return {
  savingsRate: i.avgMonthlyIncome > 0 ? ((i.avgMonthlyIncome - i.avgMonthlyExpense) / i.avgMonthlyIncome) * 100 : null,
  emergencyMonths: emergencyBase > 0 ? i.emergencyFund / emergencyBase : null,
  emergencyBasis,
  debtServiceRatio: i.avgMonthlyIncome > 0 ? (i.monthlyDebtPayment / i.avgMonthlyIncome) * 100 : null,
  debtToAssets: i.totalAssets > 0 ? i.totalLiabilities / i.totalAssets : null
  };
};

export type HealthStatus = 'good' | 'warn' | 'bad';

export interface HealthComponent {
  key: 'savings' | 'emergency' | 'debtService' | 'leverage';
  label: string;
  value: number;
  /** 0–100 */
  score: number;
  weight: number;
  status: HealthStatus;
  target: string;
}

export interface HealthScore {
  /** 0–100; null khi chưa đủ dữ liệu cho bất kỳ thành phần nào */
  score: number | null;
  status: HealthStatus | null;
  components: HealthComponent[];
}

const clamp = (x: number, lo = 0, hi = 100) => Math.min(hi, Math.max(lo, x));
/** nội suy tuyến tính: value ở `from` → 0 điểm, ở `to` → 100 điểm */
const ramp = (value: number, from: number, to: number) => clamp(((value - from) / (to - from)) * 100);
export const statusOf = (score: number): HealthStatus => (score >= 70 ? 'good' : score >= 40 ? 'warn' : 'bad');

export const healthScore = (r: HealthRatios): HealthScore => {
  const parts: Array<Omit<HealthComponent, 'status'>> = [];
  if (r.savingsRate !== null) {
    parts.push({ key: 'savings', label: 'Tỷ lệ tiết kiệm', value: r.savingsRate, score: ramp(r.savingsRate, 0, 20), weight: 30, target: '≥ 20% thu nhập' });
  }
  if (r.emergencyMonths !== null) {
    parts.push({ key: 'emergency', label: 'Quỹ khẩn cấp', value: r.emergencyMonths, score: ramp(r.emergencyMonths, 0, 6), weight: 30, target: r.emergencyBasis === 'essential' ? '3–6 tháng chi thiết yếu' : '3–6 tháng chi tiêu' });
  }
  if (r.debtServiceRatio !== null) {
    parts.push({ key: 'debtService', label: 'Gánh nặng trả nợ', value: r.debtServiceRatio, score: ramp(r.debtServiceRatio, 36, 15), weight: 20, target: '≤ 15% (tối đa 36%) thu nhập' });
  }
  if (r.debtToAssets !== null) {
    parts.push({ key: 'leverage', label: 'Nợ so với tài sản', value: r.debtToAssets, score: ramp(r.debtToAssets, 1, 0.3), weight: 20, target: '≤ 30% tài sản' });
  }
  const components: HealthComponent[] = parts.map(p => ({ ...p, status: statusOf(p.score) }));
  if (!components.length) return { score: null, status: null, components };
  const wsum = components.reduce((s, c) => s + c.weight, 0);
  const score = components.reduce((s, c) => s + c.score * c.weight, 0) / wsum;
  return { score: Math.round(score), status: statusOf(score), components };
};

/**
 * Gánh nặng trả nợ ĐỊNH KỲ hằng tháng (ước tính, không phụ thuộc đã trả tháng này chưa):
 * Σ monthlyPayment của nợ còn hiệu lực + dư nợ thẻ × % tối thiểu (mặc định 5%).
 * Khác `computeMonthlyObligations` (còn PHẢI TRẢ tháng này, đã trừ phần trả rồi) —
 * hàm này đo "gánh nặng", dùng cho tỷ số sức khoẻ.
 */
export const monthlyDebtLoad = (
  debts: Array<{ status?: string; type?: string; monthlyPayment?: number | null; remainingBalance?: number; creditCardId?: string | null }>,
  sources: Array<{ id?: string; type?: string; usedCredit?: number; minimumPaymentPercent?: number | string }>
): number => {
  const linked = cardLinkedLoanBalance(debts);
  const loans = debts
    .filter(d => d.type !== 'credit' && (d.status === 'active' || d.status === 'overdue'))
    .reduce((s, d) => s + (Number(d.monthlyPayment) || 0), 0);
  const cards = sources
    .filter(s => s.type === 'credit')
    .reduce((s, c) => s + cardOwnBalance(Number(c.usedCredit) || 0, linked.get(c.id ?? '') ?? 0) * ((Number(c.minimumPaymentPercent) || 5) / 100), 0);
  return loans + cards;
};
